import { CompareArrows, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import DiffViewer from 'react-diff-viewer-continued';

/**
 * Collapsible original vs updated transcript diff (live `updatedTranscript` from the form).
 */
export default function QcTranscriptDiffPanel({
  expanded,
  onToggle,
  oldValue,
  committedChangePercentage = 0,
  readOnly = false,
  dir = 'ltr',
}) {
  const theme = useTheme();
  const splitView = useMediaQuery(theme.breakpoints.up('md'));
  const { control } = useFormContext();
  const newValue = useWatch({
    control,
    name: 'updatedTranscript',
    defaultValue: '',
  });
  const newStr =
    typeof newValue === 'string' ? newValue : String(newValue ?? '');

  const showDiffBody = useMemo(() => {
    const o = String(oldValue ?? '');
    return o.length > 0 || newStr.length > 0;
  }, [oldValue, newStr]);

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor:
          t.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : t.palette.grey[50],
        overflow: 'hidden',
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          px: { xs: 1, sm: 1.25 },
          py: { xs: 0.75, sm: 1 },
          cursor: 'pointer',
        }}
        onClick={() => onToggle?.(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.(!expanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <CompareArrows
          sx={{
            fontSize: 18,
            color: 'primary.main',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'text.primary',
              display: 'block',
              lineHeight: 1.3,
            }}
          >
            Original vs updated
            {committedChangePercentage > 0 && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 1,
                  fontWeight: 800,
                  color: 'primary.main',
                  textTransform: 'none',
                  letterSpacing: 'normal',
                }}
              >
                (Change: {committedChangePercentage.toFixed(2)}%)
              </Typography>
            )}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem' }}
          >
            {expanded ? 'Hide diff' : 'Show side-by-side diff'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse diff' : 'Expand diff'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(!expanded);
          }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        {showDiffBody ? (
          <Box
            dir={dir}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: { xs: 0.5, sm: 1 },
              pb: { xs: 1, sm: 1.25 },
              maxHeight: { xs: '42vh', sm: '48vh', md: '52vh', lg: '56vh' },
              overflow: 'auto',
            }}
          >
            <DiffViewer
              oldValue={String(oldValue ?? '')}
              newValue={newStr}
              splitView={splitView}
              hideLineNumbers={false}
              useDarkTheme={theme.palette.mode === 'dark'}
              leftTitle="Original"
              rightTitle="Updated"
              showDiffOnly={false}
              disabled={readOnly}
              styles={{
                variables: {
                  dark: {
                    diffViewerBackground: theme.palette.background.default,
                    addedBackground: 'rgba(46, 160, 67, 0.18)',
                    removedBackground: 'rgba(248, 81, 73, 0.18)',
                    wordDiff: {
                      addedBackground: 'rgba(46, 160, 67, 0.38)',
                      removedBackground: 'rgba(248, 81, 73, 0.38)',
                    },
                  },
                  light: {
                    diffViewerBackground: '#fafafa',
                    addedBackground: '#e6ffed',
                    removedBackground: '#ffeef0',
                    wordDiff: {
                      addedBackground: '#acf2bd',
                      removedBackground: '#fdb8c0',
                    },
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              px: 1.5,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Nothing to compare yet.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
